import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CreditCard,
  Loader2,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";

import Sidebar from "@/components/Sidebar";
import DashboardFooter from "@/components/DashboardFooter";
import BillingCatalog from "@/pages/Billing/BillingCatalog";
import BillingMismatchBanner from "@/pages/Billing/BillingMismatchBanner";
import SwitchPlanDialog from "@/pages/Billing/SwitchPlanDialog";
import { formatMinorAmount } from "@/constants/billingCatalog";
import { getClientBillingTier } from "@/constants/clientBillingTiers";
import { getClientAuthHeaders } from "@/lib/apiAuth";

/**
 * Billing / Subscription page.
 *
 * Backend contract:
 *   GET  /api/billing/overview     -> { billing_country, catalog, subscription } (снимок + каталог)
 *   POST /api/stripe/portal        -> { url }
 *   POST /api/stripe/checkout      -> { url, session_id }  body: { tariff_id }
 *
 * Snapshot (if not inactive) sits above the country catalog. Catalog is always shown.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Small helpers -------------------------------------------------------

/** Pretty-print an ISO date (e.g. "September 14, 2025"). Empty string if invalid. */
const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/** Extract a user-friendly message out of an axios error. */
const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

// ---- Loading / skeleton --------------------------------------------------

const SubscriptionSkeleton = () => (
  <div
    className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 animate-pulse"
    data-testid="billing-skeleton"
  >
    <div className="flex items-center justify-between mb-6">
      <div className="h-6 w-40 bg-white/10 rounded" />
      <div className="h-6 w-20 bg-white/10 rounded-full" />
    </div>
    <div className="space-y-4">
      <div className="h-4 w-2/3 bg-white/10 rounded" />
      <div className="h-4 w-1/2 bg-white/10 rounded" />
      <div className="h-4 w-1/3 bg-white/10 rounded" />
    </div>
    <div className="mt-8 h-12 w-60 bg-white/10 rounded-lg" />
  </div>
);

// ---- State B / C: active subscription -----------------------------------

const StatusBadge = ({ variant, label }) => {
  const styles = {
    active:
      "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
    canceling:
      "bg-amber-500/10 text-amber-400 border border-amber-500/30",
    neutral: "bg-white/5 text-[#9CA3AF] border border-white/10",
  }[variant] || "bg-white/5 text-[#9CA3AF] border border-white/10";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-heading font-semibold uppercase tracking-wide ${styles}`}
      data-testid={`billing-status-badge-${variant}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
};

const ActiveSubscriptionCard = ({
  subscription,
  onManage,
  isSubmitting,
  errorMessage,
}) => {
  const isCanceling = !!subscription.cancel_at_period_end;
  const periodEndPretty = formatDate(subscription.current_period_end);
  const tier = subscription.legacy_tier ?? subscription.tier;
  const tierMeta = tier ? getClientBillingTier(tier) : null;
  const creditsIncluded =
    subscription.credits_included ?? tierMeta?.credits ?? null;

  return (
    <div
      className={`rounded-2xl border bg-white/[0.02] p-6 md:p-8 ${
        isCanceling ? "border-amber-500/30" : "border-white/10"
      }`}
      data-testid={
        isCanceling ? "billing-active-canceling" : "billing-active-card"
      }
    >
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-heading mb-1">
            Current Plan
          </p>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6]">
            {subscription.plan_name || tierMeta?.name || "AI Broker"}
          </h2>
          {tierMeta?.badge && (
            <p className="mt-2 text-[10px] font-mono uppercase tracking-wider text-[#00D1FF]">
              {tierMeta.badge}
            </p>
          )}
        </div>
        {isCanceling ? (
          <StatusBadge variant="canceling" label="Canceling" />
        ) : (
          <StatusBadge variant="active" label="Active" />
        )}
      </div>

      {/* Pending-cancellation alert */}
      {isCanceling && (
        <div
          className="mt-6 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
          data-testid="billing-cancel-alert"
          role="alert"
        >
          <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-100 leading-relaxed">
            Your subscription will be canceled
            {periodEndPretty ? ` on ${periodEndPretty}` : " at the end of the current period"}.
            You will lose access to the AI Broker.
          </p>
        </div>
      )}

      {/* Details grid */}
      <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
          <dt className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
            {isCanceling ? "Access ends" : "Next renewal"}
          </dt>
          <dd className="font-heading font-semibold text-[#F3F4F6] text-base">
            {periodEndPretty || "—"}
          </dd>
        </div>
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
          <dt className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
            Billed
          </dt>
          <dd
            className="font-heading font-semibold text-[#F3F4F6] text-base"
            data-testid="billing-billed-amount"
          >
            {subscription.amount_minor == null
              ? "—"
              : formatMinorAmount(
                  subscription.amount_minor,
                  subscription.currency
                )}
          </dd>
        </div>
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
          <dt className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
            AI Credits / mo
          </dt>
          <dd className="font-heading font-semibold text-[#F3F4F6] text-base">
            {creditsIncluded != null ? creditsIncluded.toLocaleString() : "—"}
          </dd>
        </div>
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
          <dt className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
            Status
          </dt>
          <dd className="font-heading font-semibold text-[#F3F4F6] text-base capitalize">
            {subscription.status?.replace(/_/g, " ") || "—"}
          </dd>
        </div>
      </dl>

      {/* CTA */}
      <div className="mt-8 flex flex-col items-start gap-3">
        <button
          type="button"
          onClick={onManage}
          disabled={isSubmitting}
          className={`inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-heading font-bold text-sm md:text-base transition-colors duration-300 ${
            isSubmitting
              ? "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
              : isCanceling
              ? "bg-amber-400 text-[#0F1115] hover:bg-amber-300"
              : "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
          }`}
          data-testid={
            isCanceling ? "billing-renew-button" : "billing-manage-button"
          }
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Redirecting…
            </>
          ) : isCanceling ? (
            <>
              <RefreshCw className="h-4 w-4" />
              Renew Subscription
            </>
          ) : (
            <>
              <CreditCard className="h-4 w-4" />
              Manage or Cancel Subscription
            </>
          )}
        </button>

        {!isCanceling && (
          <p className="text-xs text-[#9CA3AF]">
            Update payment method or cancel. Changing country or picking a catalog
            plan does not move this subscription.
          </p>
        )}

        {errorMessage && (
          <p
            className="text-sm text-red-400"
            role="alert"
            data-testid="billing-error-message"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

// ---- Error / unauthenticated fallback -----------------------------------

const PageErrorState = ({ message, onRetry, isRetrying }) => (
  <div
    className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center"
    data-testid="billing-load-error"
  >
    <AlertTriangle className="mx-auto h-10 w-10 text-red-400 mb-4" />
    <h2 className="font-heading font-bold text-xl text-[#F3F4F6] mb-2">
      Couldn't load your subscription
    </h2>
    <p className="text-sm text-[#9CA3AF] max-w-md mx-auto">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      disabled={isRetrying}
      className={`mt-6 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-heading font-semibold transition-colors ${
        isRetrying
          ? "bg-white/5 text-[#9CA3AF] cursor-not-allowed"
          : "bg-white/10 text-[#F3F4F6] hover:bg-white/20"
      }`}
      data-testid="billing-retry-button"
    >
      {isRetrying ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
      Retry
    </button>
  </div>
);

// ---- Page ----------------------------------------------------------------

const Billing = () => {
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingTariffId, setSubmittingTariffId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [switchOffer, setSwitchOffer] = useState(null);
  const [switchPreview, setSwitchPreview] = useState(null);
  const [switchPreviewError, setSwitchPreviewError] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const checkoutInFlightRef = useRef(false);

  const fetchOverview = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/billing/overview`, {
        headers: getClientAuthHeaders(),
      });
      setOverview(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      setLoadError(
        extractErrorMessage(
          err,
          "Something went wrong while loading your subscription. Please try again."
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const startCheckout = async (tariffId) => {
    if (checkoutInFlightRef.current) return;
    checkoutInFlightRef.current = true;
    setIsSubmitting(true);
    setSubmittingTariffId(tariffId);
    setActionError("");
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/stripe/checkout`,
        { tariff_id: tariffId },
        { headers: getClientAuthHeaders() }
      );
      const url = res?.data?.url;
      if (!url) {
        throw new Error("Invalid checkout response");
      }
      window.location.href = url;
    } catch (err) {
      checkoutInFlightRef.current = false;
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      if (err?.response?.status === 409 && err?.response?.data?.detail === "use_plan_switch") {
        setIsSubmitting(false);
        setSubmittingTariffId(null);
        const offer = (overview?.catalog?.items ?? []).find((item) => item.tariff_id === tariffId);
        if (offer?.amount_minor != null) setSwitchOffer(offer);
        return;
      }
      setActionError(extractErrorMessage(err, "We couldn't start the checkout. Please try again in a moment."));
      if (err?.response?.status === 409) fetchOverview();
      setIsSubmitting(false);
      setSubmittingTariffId(null);
    }
  };

  useEffect(() => {
    if (!switchOffer) {
      setSwitchPreview(null);
      setSwitchPreviewError("");
      setIsPreviewLoading(false);
      return;
    }
    const tariffId = switchOffer.tariff_id;
    let cancelled = false;
    setIsPreviewLoading(true);
    setSwitchPreview(null);
    setSwitchPreviewError("");
    axios
      .post(`${BACKEND_URL}/api/stripe/switch/preview`, { tariff_id: tariffId }, { headers: getClientAuthHeaders() })
      .then((res) => {
        if (cancelled) return;
        setSwitchPreview(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          Cookies.remove("token");
          Cookies.remove("email");
          navigate("/login");
          return;
        }
        setSwitchPreviewError(
          extractErrorMessage(err, "We couldn't estimate the switch amount. Please try again.")
        );
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [switchOffer, navigate]);

  const handleSwitchConfirm = async () => {
    if (!switchPreview || !switchOffer || isSubmitting) return;
    const tariffId = switchOffer.tariff_id;
    const preview = switchPreview;
    if (preview.settlement === "replace") {
      setSwitchOffer(null);
      startCheckout(tariffId);
      return;
    }
    if (preview.settlement !== "prorate") return;
    if (preview.proration_date == null) return;
    setIsSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/stripe/switch`,
        { tariff_id: tariffId, proration_date: preview.proration_date },
        { headers: getClientAuthHeaders() }
      );
      setSwitchOffer(null);
      fetchOverview();
      setIsSubmitting(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      if (err?.response?.status === 409 && err?.response?.data?.detail === "preview_expired") {
        setIsSubmitting(false);
        setIsPreviewLoading(true);
        setSwitchPreview(null);
        setSwitchPreviewError("");
        try {
          const res = await axios.post(
            `${BACKEND_URL}/api/stripe/switch/preview`,
            { tariff_id: tariffId },
            { headers: getClientAuthHeaders() }
          );
          setSwitchPreview(res.data);
        } catch (previewErr) {
          if (previewErr?.response?.status === 401) {
            Cookies.remove("token");
            Cookies.remove("email");
            navigate("/login");
            return;
          }
          setSwitchPreviewError(
            extractErrorMessage(
              previewErr,
              "We couldn't estimate the switch amount. Please try again."
            )
          );
        } finally {
          setIsPreviewLoading(false);
        }
        return;
      }
      setSwitchOffer(null);
      setIsSubmitting(false);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail === "use_checkout") {
        startCheckout(tariffId);
        return;
      }
      if (status === 409 && detail === "Already on this plan") {
        fetchOverview();
        return;
      }
      if (
        status === 402 ||
        (status === 409 && detail === "Update your payment method before switching plans")
      ) {
        setActionError(
          extractErrorMessage(
            err,
            "Could not charge the card. Update your payment method in Manage subscription."
          )
        );
        return;
      }
      setActionError(
        extractErrorMessage(
          err,
          "We couldn't switch your plan. Please try again in a moment."
        )
      );
    }
  };

  const handlePlanAction = (tariffId) => {
    const items = overview?.catalog?.items ?? [];
    const offer = items.find((item) => item.tariff_id === tariffId);
    if (!offer) return;
    if (offer.checkout_action === "subscribe") {
      startCheckout(tariffId);
      return;
    }
    if (offer.checkout_action === "switch") {
      if (offer.amount_minor == null) return;
      setSwitchOffer(offer);
    }
  };

  const openPortal = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setActionError("");
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/stripe/portal`,
        {},
        { headers: getClientAuthHeaders() }
      );
      const url = res?.data?.url;
      if (!url) {
        throw new Error("Invalid portal response");
      }
      window.location.href = url;
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      setActionError(
        extractErrorMessage(
          err,
          "We couldn't open the billing portal. Please try again in a moment."
        )
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F1115] flex"
      data-testid="billing-page"
    >
      <Sidebar />

      <div className="flex-1 md:ml-64">
        <main className="p-4 md:p-6 lg:p-8 max-w-[1100px] mx-auto">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-1">
              Billing &amp; Subscription
            </h1>
            <p className="text-[#9CA3AF] font-body text-sm">
              Manage your Digital Assistant plan, payment method and renewal schedule.
            </p>
          </div>

          {isLoading ? (
            <SubscriptionSkeleton />
          ) : loadError ? (
            <PageErrorState
              message={loadError}
              onRetry={fetchOverview}
              isRetrying={isLoading}
            />
          ) : (
            <div className="space-y-8">
              <Link
                to="/dashboard/account-settings"
                className="inline-block text-sm font-heading text-[#00D1FF] hover:underline"
              >
                Change billing country
              </Link>
              {overview.subscription.mismatch_with_catalog ? (
                <BillingMismatchBanner
                  planName={overview.subscription.plan_name}
                  amountMinor={overview.subscription.amount_minor}
                  currency={overview.subscription.currency}
                />
              ) : null}

              {overview.subscription.status !== "inactive" ? (
                <ActiveSubscriptionCard
                  subscription={overview.subscription}
                  onManage={openPortal}
                  isSubmitting={isSubmitting}
                  errorMessage={actionError}
                />
              ) : null}

              <BillingCatalog
                catalog={overview.catalog}
                onPlanAction={(tariffId) => {
                  if (isSubmitting) return;
                  handlePlanAction(tariffId);
                }}
                isSubmitting={isSubmitting}
                submittingTariffId={submittingTariffId}
                errorMessage={actionError}
              />
              <SwitchPlanDialog
                open={Boolean(switchOffer)}
                offer={switchOffer}
                currentLegacyTier={overview?.subscription?.legacy_tier ?? null}
                preview={switchPreview}
                previewError={switchPreviewError}
                isPreviewLoading={isPreviewLoading}
                isSubmitting={isSubmitting}
                onConfirm={handleSwitchConfirm}
                onCancel={() => setSwitchOffer(null)}
              />
            </div>
          )}
        </main>
        <DashboardFooter />
      </div>
    </div>
  );
};

export default Billing;
